import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
    useParams,
} from "react-router-dom";

import {
    Alert,
    Badge,
    Button,
    Card,
    Select,
    Container,
    Grid,
    Group,
    Loader,
    NumberInput,
    Paper,
    ScrollArea,
    Stack,
    Text,
    Title,
} from "@mantine/core";

import { notifications } from "@mantine/notifications";

import { getAuthHeaders } from "../../../api/authHelper";

import "./PracticePlannerPage.css";


interface PlannerLocation {
    practicePlanLocationId: number;
    practiceLocationId: number;
    locationName: string;
    numberOfCourts: number;
}

interface PlannerCourt {
    locationId: number;
    courtNumber: number;
    locationName: string;
}

interface PlannerRequest {
    practiceId: number;
    country: string;
    practiceDate?: string;
    practiceTime?: number;
    practiceTimeText: string;
    practiceLocation?: number;
    practiceLocationText: string;
    numberOfCourts: number;
    numberOfPlayers: number;
    numberOfMinutes: number;
    isPlanned: boolean;
}


interface PlannerBooking {
    practicePlanBookingId: number;
    practiceId: number;
    country: string;
    practiceLocationId: number;
    locationName: string;
    startTime: string;
    endTime: string;
    courtFrom?: number;
    courtTo?: number;
    numberOfCourts: number;
    numberOfPlayers?: number;
    note?: string;
}


interface PlannerDto {
    practicePlanId: number;
    eventId: number;
    practiceDate: string;
    status: string;
    locations: PlannerLocation[];
    requests: PlannerRequest[];
    bookings: PlannerBooking[];
}

interface PlannerListItem {
    practicePlanId: number;
    practiceDate: string;
    status: string;
}

interface EventDto {
    eventID: number;
    eventName: string;
}

// ---------------------------------------------------------
// BOARD SETTINGS
// ---------------------------------------------------------

const SLOT_MINUTES = 30;
const SLOT_HEIGHT = 100;

const BOARD_START_HOUR = 8;
const BOARD_END_HOUR = 22;

const BOARD_START_MINUTES =
    BOARD_START_HOUR * 60;


// ---------------------------------------------------------
// TIME HELPERS
// ---------------------------------------------------------

const timeToMinutes = (time: string) => {
    const cleanTime =
        time.slice(0, 5);

    const [hours, minutes] =
        cleanTime
            .split(":")
            .map(Number);

    return (
        hours * 60 +
        minutes
    );
};


const getBookingTop = (
    booking: PlannerBooking
) => {
    const startMinutes =
        timeToMinutes(
            booking.startTime
        );

    const minutesFromBoardStart =
        startMinutes -
        BOARD_START_MINUTES;

    return (
        minutesFromBoardStart /
        SLOT_MINUTES
    ) * SLOT_HEIGHT;
};


const getBookingHeight = (
    booking: PlannerBooking
) => {
    const startMinutes =
        timeToMinutes(
            booking.startTime
        );

    const endMinutes =
        timeToMinutes(
            booking.endTime
        );

    const duration =
        endMinutes -
        startMinutes;

    return Math.max(
        40,
        (
            duration /
            SLOT_MINUTES
        ) * SLOT_HEIGHT
    );
};

const getBookingCourtFrom = (
    booking: PlannerBooking
) => {
    if (
        booking.courtFrom !== undefined &&
        booking.courtFrom !== null
    ) {
        return booking.courtFrom;
    }

    return 1;
};


const getBookingCourtTo = (
    booking: PlannerBooking
) => {
    if (
        booking.courtTo !== undefined &&
        booking.courtTo !== null
    ) {
        return booking.courtTo;
    }

    return (
        getBookingCourtFrom(booking) +
        booking.numberOfCourts -
        1
    );
};


const bookingOccupiesCourt = (
    booking: PlannerBooking,
    courtNumber: number
) => {
    const courtFrom =
        getBookingCourtFrom(booking);

    const courtTo =
        getBookingCourtTo(booking);

    return (
        courtNumber >= courtFrom &&
        courtNumber <= courtTo
    );
};


// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const PracticePlannerPage: React.FC = () => {

    const {
        eventId,
        planId,
    } = useParams();

    const practicePlanId =
        Number(planId);

    const currentEventId =
        Number(eventId);

    const apiBase =
        process.env.REACT_APP_API_URL;

    const API_URL =
        `${apiBase}/api/practice-planner`;


    const [planner, setPlanner] =
        useState<PlannerDto | null>(
            null
        );

    const [loading, setLoading] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [courtsToAdd, setCourtsToAdd] =
        useState<Record<number, number>>({});

    const [error, setError] =
        useState<string | null>(
            null
        );
    const [plans, setPlans] =
        useState<PlannerListItem[]>([]);

    const [currentEvent, setCurrentEvent] =
        useState<EventDto | null>(null);

    const [events, setEvents] =
        useState<EventDto[]>([]);

    const navigate =
        useNavigate();


    // ---------------------------------------------------------
    // TIME SLOTS
    // ---------------------------------------------------------

    const timeSlots =
        useMemo(() => {

            const result: string[] =
                [];

            for (
                let minutes =
                    BOARD_START_HOUR * 60;

                minutes <=
                BOARD_END_HOUR * 60;

                minutes +=
                SLOT_MINUTES
            ) {

                const hour =
                    Math.floor(
                        minutes / 60
                    );

                const minute =
                    minutes % 60;

                result.push(
                    `${String(hour).padStart(
                        2,
                        "0"
                    )}:${String(
                        minute
                    ).padStart(
                        2,
                        "0"
                    )}`
                );
            }

            return result;

        }, []);

    const courts = useMemo(() => {
        if (!planner) {
            return [];
        }

        const result: PlannerCourt[] = [];

        planner.locations.forEach((location) => {
            for (let court = 1; court <= location.numberOfCourts; court++) {
                result.push({
                    locationId: location.practiceLocationId,
                    courtNumber: court,
                    locationName: location.locationName,
                });
            }
        });

        return result;
    }, [planner]);

    // ---------------------------------------------------------
    // LOAD PLANS
    // ---------------------------------------------------------
    // ---------------------------------------------------------
    // LOAD EVENT
    // ---------------------------------------------------------

    useEffect(() => {

        const loadEvents =
            async () => {

                try {

                    const res =
                        await fetch(
                            `${apiBase}/api/Events`,
                            {
                                headers:
                                    getAuthHeaders() as Record<
                                        string,
                                        string
                                    >,
                            }
                        );

                    if (!res.ok) {
                        return;
                    }

                    const data: EventDto[] =
                        await res.json();


                    setEvents(data);

                } catch (err) {

                    console.error(err);

                }
            };

        loadEvents();

    }, []);

    useEffect(() => {

        if (!currentEventId) {
            setCurrentEvent(null);
            return;
        }

        const event =
            events.find(
                (event) =>
                    event.eventID === currentEventId
            );

        setCurrentEvent(
            event ?? null
        );

    }, [currentEventId, events]);

    useEffect(() => {

        if (!currentEventId) {
            setPlans([]);
            return;
        }

        const loadPlans =
            async () => {

                try {

                    const res =
                        await fetch(
                            `${API_URL}/event/${currentEventId}`,
                            {
                                headers:
                                    getAuthHeaders() as Record<
                                        string,
                                        string
                                    >,
                            }
                        );

                    // No plans for this event
                    if (!res.ok) {
                        setPlans([]);
                        return;
                    }

                    const data =
                        await res.json();

                    setPlans(
                        Array.isArray(data)
                            ? data
                            : []
                    );

                } catch (err) {

                    console.error(err);

                    setPlans([]);

                }
            };

        loadPlans();

    }, [currentEventId]);
    
    // ---------------------------------------------------------
    // LOAD
    // ---------------------------------------------------------

    useEffect(() => {

        if (practicePlanId === 0) {
            return;
        }

        loadPlanner();

    }, [practicePlanId]);


    const loadPlanner =
        async () => {

            try {

                setLoading(true);
                setError(null);

                const res =
                    await fetch(
                        `${API_URL}/${practicePlanId}`,
                        {
                            headers:
                                getAuthHeaders() as Record<
                                    string,
                                    string
                                >,
                        }
                    );


                if (!res.ok) {

                    const message =
                        await res.text();

                    throw new Error(
                        message ||
                        "Kunne ikke hente planen."
                    );
                }


                const data: PlannerDto =
                    await res.json();

                setPlanner(data);

            } catch (err: any) {

                console.error(err);

                setError(
                    err?.message ||
                    "Kunne ikke hente planen."
                );

            } finally {

                setLoading(false);
            }
        };


    // ---------------------------------------------------------
    // UNPLANNED
    // ---------------------------------------------------------

    const unplannedRequests =
        useMemo(() => {

            if (!planner) {
                return [];
            }

            return planner.requests.filter(
                (request) => {

                    const plannedCourts =
                        planner.bookings
                            .filter(
                                (booking) =>
                                    booking.practiceId ===
                                    request.practiceId
                            )
                            .reduce(
                                (
                                    total,
                                    booking
                                ) =>
                                    total +
                                    booking.numberOfCourts,
                                0
                            );

                    return (
                        plannedCourts <
                        request.numberOfCourts
                    );
                }
            );

        }, [planner]);

    const getRemainingCourts = (
        request: PlannerRequest
    ) => {

        if (!planner) {
            return request.numberOfCourts;
        }

        const plannedCourts =
            planner.bookings
                .filter(
                    (booking) =>
                        booking.practiceId ===
                        request.practiceId
                )
                .reduce(
                    (
                        total,
                        booking
                    ) =>
                        total +
                        booking.numberOfCourts,
                    0
                );

        return Math.max(
            0,
            request.numberOfCourts -
            plannedCourts
        );
    };


    // ---------------------------------------------------------
    // DROP REQUEST
    // ---------------------------------------------------------

    const handleDropRequest =
        async (
            event: React.DragEvent,
            locationId: number,
            courtNumber: number,
            startTime: string
        ) => {

            event.preventDefault();

            const practiceId =
                Number(
                    event.dataTransfer.getData(
                        "practiceId"
                    )
                );


            if (
                !practiceId ||
                !planner
            ) {
                return;
            }


            const request =
                planner.requests.find(
                    (x) =>
                        x.practiceId ===
                        practiceId
                );


            if (!request) {
                return;
            }


            const remainingCourts =
                getRemainingCourts(
                    request
                );


            if (remainingCourts <= 0) {
                return;
            }


            /*
             * Get the number of courts selected
             * by the user.
             */
            const courtsToBook =
                courtsToAdd[request.practiceId] ??
                remainingCourts;


            /*
             * Never allow more courts than
             * the request still needs.
             */
            if (
                courtsToBook < 1 ||
                courtsToBook > remainingCourts
            ) {

                notifications.show({
                    title:
                        "Ugyldigt antal baner",

                    message:
                        `Du kan højst vælge ${remainingCourts} baner.`,

                    color:
                        "red",
                });

                return;
            }


            const location =
                planner.locations.find(
                    (x) =>
                        x.practiceLocationId ===
                        locationId
                );


            if (!location) {
                return;
            }


            /*
             * Calculate exactly which courts
             * the user is trying to book.
             */
            const courtFrom =
                courtNumber;

            const courtTo =
                courtFrom +
                courtsToBook -
                1;


            /*
             * Make sure the requested court
             * range exists in the location.
             */
            if (
                courtTo >
                location.numberOfCourts
            ) {

                notifications.show({
                    title:
                        "Ikke nok baner",

                    message:
                        `Der er ikke plads til ${courtsToBook} baner fra bane ${courtFrom}.`,

                    color:
                        "red",
                });

                return;
            }


            /*
             * Check every requested court
             * for conflicts.
             */
            for (
                let court = courtFrom;
                court <= courtTo;
                court++
            ) {

                const courtHasConflict =
                    planner.bookings.some(
                        (booking) => {

                            if (
                                booking.practiceLocationId !==
                                locationId
                            ) {
                                return false;
                            }


                            const bookingStart =
                                timeToMinutes(
                                    booking.startTime
                                );

                            const bookingEnd =
                                timeToMinutes(
                                    booking.endTime
                                );

                            const dropStart =
                                timeToMinutes(
                                    startTime
                                );

                            const dropEnd =
                                dropStart +
                                request.numberOfMinutes;


                            const overlapsTime =
                                bookingStart <
                                dropEnd &&
                                bookingEnd >
                                dropStart;


                            if (!overlapsTime) {
                                return false;
                            }


                            return bookingOccupiesCourt(
                                booking,
                                court
                            );
                        }
                    );


                if (courtHasConflict) {

                    notifications.show({
                        title:
                            "Bane optaget",

                        message:
                            `Bane ${court} er allerede optaget på det valgte tidspunkt.`,

                        color:
                            "red",
                    });

                    return;
                }
            }


            try {

                setSaving(true);


                const res =
                    await fetch(
                        `${API_URL}/${planner.practicePlanId}/bookings`,
                        {
                            method:
                                "POST",

                            headers:
                                getAuthHeaders({
                                    "Content-Type":
                                        "application/json",
                                }) as Record<
                                    string,
                                    string
                                >,

                            body:
                                JSON.stringify({
                                    practiceId:
                                        request.practiceId,

                                    practiceLocationId:
                                        locationId,

                                    startTime,

                                    numberOfCourts:
                                        courtsToBook,

                                    numberOfPlayers:
                                        request.numberOfPlayers,

                                    numberOfMinutes:
                                        request.numberOfMinutes,

                                    courtFrom,

                                    courtTo,

                                    note:
                                        null,
                                }),
                        }
                    );


                if (!res.ok) {

                    const message =
                        await res.text();

                    throw new Error(
                        message ||
                        "Kunne ikke placere ønsket."
                    );
                }


                notifications.show({
                    title:
                        "Tilføjet",

                    message:
                        `${request.country} er placeret på bane ${courtFrom}-${courtTo} kl. ${startTime}`,

                    color:
                        "green",
                });


                await loadPlanner();

            } catch (err: any) {

                notifications.show({
                    title:
                        "Kan ikke placeres",

                    message:
                        err?.message ||
                        "Der opstod en fejl.",

                    color:
                        "red",
                });

            } finally {

                setSaving(false);
            }
        };


    // ---------------------------------------------------------
    // REMOVE
    // ---------------------------------------------------------

    const removeBooking =
        async (
            booking: PlannerBooking
        ) => {

            if (!planner) {
                return;
            }


            try {

                const res =
                    await fetch(
                        `${API_URL}/${planner.practicePlanId}/bookings/${booking.practicePlanBookingId}`,
                        {
                            method:
                                "DELETE",

                            headers:
                                getAuthHeaders() as Record<
                                    string,
                                    string
                                >,
                        }
                    );


                if (!res.ok) {

                    throw new Error(
                        "Kunne ikke fjerne bookingen."
                    );
                }


                await loadPlanner();

            } catch (err: any) {

                notifications.show({
                    title:
                        "Fejl",

                    message:
                        err?.message ||
                        "Kunne ikke fjerne booking.",

                    color:
                        "red",
                });
            }
        };


    // ---------------------------------------------------------
    // PUBLISH
    // ---------------------------------------------------------

    const publishPlan =
        async () => {

            if (!planner) {
                return;
            }


            try {

                setSaving(true);

                const res =
                    await fetch(
                        `${API_URL}/${planner.practicePlanId}/publish`,
                        {
                            method:
                                "POST",

                            headers:
                                getAuthHeaders() as Record<
                                    string,
                                    string
                                >,
                        }
                    );


                if (!res.ok) {

                    throw new Error(
                        "Kunne ikke publicere planen."
                    );
                }


                notifications.show({
                    title:
                        "Plan publiceret",

                    message:
                        "Træningsplanen er nu publiceret.",

                    color:
                        "green",
                });


                await loadPlanner();

            } catch (err: any) {

                notifications.show({
                    title:
                        "Fejl",

                    message:
                        err?.message ||
                        "Planen kunne ikke publiceres.",

                    color:
                        "red",
                });

            } finally {

                setSaving(false);
            }
        };


    // ---------------------------------------------------------
    // BOOKINGS OVERLAPPING SLOT
    // ---------------------------------------------------------

    const getBookingsForSlot = (
        locationId: number,
        time: string
    ) => {

        if (!planner) {
            return [];
        }


        const slotStart =
            timeToMinutes(time);

        const slotEnd =
            slotStart +
            SLOT_MINUTES;


        return planner.bookings.filter(
            (booking) => {

                if (
                    booking.practiceLocationId !==
                    locationId
                ) {
                    return false;
                }


                const bookingStart =
                    timeToMinutes(
                        booking.startTime
                    );

                const bookingEnd =
                    timeToMinutes(
                        booking.endTime
                    );


                return (
                    bookingStart <
                    slotEnd
                    &&
                    bookingEnd >
                    slotStart
                );
            }
        );
    };

    // ---------------------------------------------------------
    // NO PLAN SELECTED
    // ---------------------------------------------------------

    if (practicePlanId === 0) {

        return (
            <Container
                size="100%"
                px="md"
                py="xl"
            >

                <Stack gap="lg">

                    {/* ------------------------------------------------
                    EVENT SELECT
                ------------------------------------------------ */}

                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                    >

                        <Select
                            label="Vælg event"
                            placeholder="Vælg event"
                            value={
                                currentEventId
                                    ? String(currentEventId)
                                    : null
                            }
                            data={events
                                .filter(
                                    (event) =>
                                        event.eventID !== undefined &&
                                        event.eventID !== null
                                )
                                .map((event) => ({
                                    value:
                                        String(event.eventID),

                                    label:
                                        event.eventName,
                                }))}
                            onChange={(value) => {

                                if (!value) {
                                    return;
                                }

                                setPlanner(null);
                                setPlans([]);
                                setCurrentEvent(null);

                                navigate(
                                    `/Admin/PracticePlanner/${value}/0`
                                );

                            }}
                            searchable
                        />

                    </Paper>


                    {/* ------------------------------------------------
                    CURRENT EVENT
                ------------------------------------------------ */}

                    {currentEvent && (

                        <Paper
                            withBorder
                            radius="md"
                            p="md"
                        >

                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Event
                            </Text>

                            <Title order={2}>
                                {currentEvent.eventName}
                            </Title>

                        </Paper>

                    )}


                    {/* ------------------------------------------------
                    EXISTING PLANS
                ------------------------------------------------ */}

                    {plans.length > 0 && (

                        <Paper
                            withBorder
                            radius="md"
                            p="sm"
                        >

                            <Stack gap="sm">

                                <Text fw={600}>
                                    Træningsplans dage
                                </Text>

                                <Group
                                    gap="sm"
                                    wrap="wrap"
                                >

                                    {plans.map((plan) => (

                                        <Button
                                            key={
                                                plan.practicePlanId
                                            }
                                            variant="light"
                                            color={
                                                plan.status ===
                                                    "Published"
                                                    ? "green"
                                                    : undefined
                                            }
                                            onClick={() =>
                                                navigate(
                                                    `/Admin/PracticePlanner/${currentEventId}/${plan.practicePlanId}`
                                                )
                                            }
                                        >
                                            {new Date(
                                                plan.practiceDate
                                            ).toLocaleDateString(
                                                "da-DK",
                                                {
                                                    weekday:
                                                        "short",

                                                    day:
                                                        "2-digit",

                                                    month:
                                                        "2-digit",
                                                }
                                            )}
                                        </Button>

                                    ))}

                                </Group>

                            </Stack>

                        </Paper>

                    )}


                    {/* ------------------------------------------------
                    NO PLANS
                ------------------------------------------------ */}

                    {plans.length === 0 && currentEventId > 0 && (

                        <Paper
                            withBorder
                            radius="md"
                            p="xl"
                        >

                            <Stack
                                align="center"
                                gap="md"
                            >

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Der findes ingen træningsplans dage
                                    for dette event.
                                </Text>

                                <Button
                                    onClick={() =>
                                        navigate(
                                            "/Admin/PracticePlanner/Create",
                                            {
                                                state: {
                                                    eventId:
                                                        currentEventId,
                                                },
                                            }
                                        )
                                    }
                                >
                                    Opret ny træningsplans dag
                                </Button>

                            </Stack>

                        </Paper>

                    )}

                </Stack>

            </Container>
        );
    }

    // ---------------------------------------------------------
    // LOADING
    // ---------------------------------------------------------

    if (loading) {

        return (
            <Container
                size="xl"
                py="xl"
            >
                <Group justify="center">

                    <Loader />

                    <Text>
                        Henter træningsplan...
                    </Text>

                </Group>
            </Container>
        );
    }


    // ---------------------------------------------------------
    // ERROR
    // ---------------------------------------------------------

    if (error) {

        return (
            <Container
                size="xl"
                py="xl"
            >
                <Alert
                    color="red"
                    title="Fejl"
                >
                    {error}
                </Alert>
            </Container>
        );
    }


    if (!planner) {
        return null;
    }


    // ---------------------------------------------------------
    // RENDER
    // ---------------------------------------------------------

    return (

        <Container
            size="100%"
            px="md"
            py="xl"
        >

            <Stack gap="lg">

                {/* ------------------------------------------------
                    EVENT SELECT
                ------------------------------------------------ */}

                <Paper
                    withBorder
                    radius="md"
                    p="md"
                >

                    <Select
                        label="Vælg event"
                        placeholder="Vælg event"
                        value={
                            currentEventId
                                ? String(currentEventId)
                                : null
                        }
                        data={events.map((event) => ({
                            value:
                                String(event.eventID),

                            label:
                                event.eventName,
                        }))}
                        onChange={(value) => {

                            if (!value) {
                                return;
                            }

                            navigate(
                                `/Admin/PracticePlanner/${value}/0`
                            );

                        }}
                        searchable
                    />

                </Paper>


                {/* ------------------------------------------------
                        CURRENT EVENT
                    ------------------------------------------------ */}

                {currentEvent && (
                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                    >
                        <Text
                            size="sm"
                            c="dimmed"
                        >
                            Event
                        </Text>

                        <Title order={2}>
                            {currentEvent.eventName}
                        </Title>
                    </Paper>
                )}

                {/* ------------------------------------------------
                        PLAN DATES
                    ------------------------------------------------ */}

                {plans.length > 0 && (

                    <Paper
                        withBorder
                        radius="md"
                        p="sm"
                    >

                        <Group
                            gap="sm"
                            wrap="wrap"
                        >

                            {plans.map((plan) => {

                                const isActive =
                                    plan.practicePlanId ===
                                    practicePlanId;


                                return (

                                    <Button
                                        key={
                                            plan.practicePlanId
                                        }
                                        variant={
                                            isActive
                                                ? "filled"
                                                : "light"
                                        }
                                        color={
                                            plan.status ===
                                                "Published"
                                                ? "green"
                                                : undefined
                                        }
                                        onClick={() =>
                                            navigate(
                                                `/Admin/PracticePlanner/${planner.eventId}/${plan.practicePlanId}`
                                            )
                                        }
                                    >
                                        {new Date(
                                            plan.practiceDate
                                        ).toLocaleDateString(
                                            "da-DK",
                                            {
                                                weekday:
                                                    "short",

                                                day:
                                                    "2-digit",

                                                month:
                                                    "2-digit",
                                            }
                                        )}
                                    </Button>

                                );
                            })}

                        </Group>

                    </Paper>

                )}

                {/* ------------------------------------------------
                    HEADER
                ------------------------------------------------ */}

                <Group
                    justify="space-between"
                    align="flex-end"
                >

                    <div>

                        <Group gap="sm">

                            <Title order={1}>
                                Træningsplan
                            </Title>

                            <Badge
                                color={
                                    planner.status ===
                                        "Published"
                                        ? "green"
                                        : "yellow"
                                }
                            >
                                {planner.status}
                            </Badge>

                        </Group>


                        <Text c="dimmed">

                            {new Date(
                                planner.practiceDate
                            ).toLocaleDateString(
                                "da-DK",
                                {
                                    weekday:
                                        "long",

                                    day:
                                        "2-digit",

                                    month:
                                        "long",

                                    year:
                                        "numeric",
                                }
                            )}

                        </Text>

                    </div>


                    <Group>

                        <Button
                            variant="default"
                            onClick={
                                loadPlanner
                            }
                        >
                            Genindlæs
                        </Button>


                        <Button
                            onClick={
                                publishPlan
                            }
                            loading={
                                saving
                            }
                            disabled={
                                planner.status ===
                                "Published"
                            }
                        >
                            Publicér plan
                        </Button>

                    </Group>

                </Group>


                {/* ------------------------------------------------
                    KPI
                ------------------------------------------------ */}

                <Grid>

                    <Grid.Col
                        span={{
                            base: 6,
                            md: 3,
                        }}
                    >
                        <Card
                            withBorder
                            padding="lg"
                        >
                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Ønsker
                            </Text>

                            <Text
                                size="2rem"
                                fw={700}
                            >
                                {
                                    planner.requests.length
                                }
                            </Text>
                        </Card>
                    </Grid.Col>


                    <Grid.Col
                        span={{
                            base: 6,
                            md: 3,
                        }}
                    >
                        <Card
                            withBorder
                            padding="lg"
                        >
                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Planlagt
                            </Text>

                            <Text
                                size="2rem"
                                fw={700}
                            >
                                {
                                    planner.bookings.length
                                }
                            </Text>
                        </Card>
                    </Grid.Col>


                    <Grid.Col
                        span={{
                            base: 6,
                            md: 3,
                        }}
                    >
                        <Card
                            withBorder
                            padding="lg"
                        >
                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Mangler
                            </Text>

                            <Text
                                size="2rem"
                                fw={700}
                            >
                                {
                                    unplannedRequests.length
                                }
                            </Text>
                        </Card>
                    </Grid.Col>


                    <Grid.Col
                        span={{
                            base: 6,
                            md: 3,
                        }}
                    >
                        <Card
                            withBorder
                            padding="lg"
                        >
                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                Haller
                            </Text>

                            <Text
                                size="2rem"
                                fw={700}
                            >
                                {
                                    planner.locations.length
                                }
                            </Text>
                        </Card>
                    </Grid.Col>

                </Grid>


                {/* ------------------------------------------------
                    MAIN
                ------------------------------------------------ */}

                <div className="practice-planner-layout">


                    {/* --------------------------------------------
                        REQUESTS
                    -------------------------------------------- */}

                    <Paper
                        withBorder
                        radius="md"
                        p="md"
                        className="practice-request-panel"
                    >

                        <Stack gap="md">

                            <div>

                                <Title order={3}>
                                    Ikke planlagt
                                </Title>

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Træk et ønske over
                                    på det ønskede
                                    tidspunkt.
                                </Text>

                                <Button
                                    onClick={() =>
                                        navigate("/Admin/PracticePlanner/Create", {
                                            state: {
                                                eventId: planner.eventId,
                                                returnTo: `/Admin/PracticePlanner/${planner.eventId}/${practicePlanId}`,
                                            },
                                        })
                                    }
                                >
                                    Opret Træningsplans Dag
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={() =>
                                        navigate("/admin/Practice")
                                    }
                                >
                                    Tilbage til Statistik
                                </Button>

                            </div>


                            {unplannedRequests.length ===
                                0 ? (

                                <Alert
                                    color="green"
                                    title="Alt er planlagt"
                                >
                                    Alle ønsker er
                                    placeret.
                                </Alert>

                            ) : (

                                unplannedRequests.map(
                                    (request) => (

                                        <Card
                                            key={
                                                request.practiceId
                                            }
                                            withBorder
                                            draggable
                                            onDragStart={(
                                                event
                                            ) => {

                                                event.dataTransfer.setData(
                                                    "practiceId",
                                                    String(
                                                        request.practiceId
                                                    )
                                                );

                                                event.dataTransfer.effectAllowed =
                                                    "move";
                                            }}
                                            className="practice-request-card"
                                        >

                                            <Stack gap={5}>

                                                <Group
                                                    justify="space-between"
                                                    align="flex-start"
                                                >

                                                    <Text
                                                        fw={700}
                                                    >
                                                        {
                                                            request.country
                                                        }
                                                    </Text>

                                                    <Badge variant="light">
                                                        {
                                                            getRemainingCourts(request)
                                                        }{" "}
                                                        baner tilbage
                                                    </Badge>
                                                    <NumberInput
                                                        label="Antal baner"
                                                        min={1}
                                                        max={getRemainingCourts(request)}
                                                        value={
                                                            courtsToAdd[request.practiceId] ??
                                                            getRemainingCourts(request)
                                                        }
                                                        onChange={(value) => {

                                                            const number =
                                                                typeof value === "number"
                                                                    ? value
                                                                    : Number(value);

                                                            setCourtsToAdd((current) => ({
                                                                ...current,

                                                                [request.practiceId]:
                                                                    Math.min(
                                                                        Math.max(
                                                                            1,
                                                                            number || 1
                                                                        ),
                                                                        getRemainingCourts(
                                                                            request
                                                                        )
                                                                    ),
                                                            }));
                                                        }}
                                                    />

                                                </Group>


                                                <Text size="sm">

                                                    Ønske:{" "}

                                                    <strong>
                                                        {
                                                            request.practiceTimeText
                                                        }
                                                    </strong>

                                                </Text>


                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    {
                                                        request.numberOfPlayers
                                                    }{" "}
                                                    spillere ·{" "}
                                                    {
                                                        request.numberOfMinutes
                                                    }{" "}
                                                    min.
                                                </Text>


                                                {request.practiceLocationText && (

                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Ønsket sted:{" "}
                                                        {
                                                            request.practiceLocationText
                                                        }
                                                    </Text>

                                                )}

                                            </Stack>

                                        </Card>
                                    )
                                )
                            )}

                        </Stack>

                    </Paper>


                    {/* --------------------------------------------
                        BOARD
                    -------------------------------------------- */}

                    <Paper
                        withBorder
                        radius="md"
                        className="practice-board-wrapper"
                    >

                        <ScrollArea
                            type="auto"
                            scrollbarSize={12}
                        >

                            <div className="practice-board">


                                {/* --------------------------------
                                    TIME COLUMN
                                -------------------------------- */}

                                <div className="practice-time-column">

                                    <div className="practice-board-header">
                                        Tid
                                    </div>


                                    {timeSlots.map(
                                        (time) => (

                                            <div
                                                key={
                                                    time
                                                }
                                                className="practice-time-cell"
                                            >
                                                {
                                                    time
                                                }
                                            </div>

                                        )
                                    )}

                                </div>


                                {/* --------------------------------
    COURTS
-------------------------------- */}

                                {courts.map((court) => (

                                    <div
                                        key={`${court.locationId}-${court.courtNumber}`}
                                        className="practice-location-column"
                                    >

                                        {/* HEADER */}

                                        <div className="practice-board-header">

                                            <Text fw={700}>
                                                Bane {court.courtNumber}
                                            </Text>

                                            <Text
                                                size="xs"
                                                c="dimmed"
                                            >
                                                {court.locationName}
                                            </Text>

                                        </div>


                                        {/* BODY */}

                                        <div className="practice-location-body">

                                            {/* ----------------
                DROP SLOTS
            ---------------- */}

                                            {timeSlots.map((time) => {

                                                const bookings =
                                                    getBookingsForSlot(
                                                        court.locationId,
                                                        time
                                                    );

                                                return (

                                                    <div
                                                        key={time}
                                                        className="practice-slot"
                                                        onDragOver={(event) => {

                                                            event.preventDefault();

                                                            event.dataTransfer.dropEffect =
                                                                "move";

                                                        }}
                                                        onDrop={(event) =>
                                                            handleDropRequest(
                                                                event,
                                                                court.locationId,
                                                                court.courtNumber,
                                                                time
                                                            )
                                                        }
                                                    >

                                                        <div className="practice-slot-capacity">
                                                            {bookings.length === 0
                                                                ? "ledig"
                                                                : ""}
                                                        </div>

                                                    </div>

                                                );
                                            })}


                                            {/* ----------------
                BOOKING BLOCKS
            ---------------- */}

                                            {planner.bookings
                                                .filter(
                                                    (booking) =>
                                                        booking.practiceLocationId ===
                                                        court.locationId &&
                                                        bookingOccupiesCourt(
                                                            booking,
                                                            court.courtNumber
                                                        )
                                                )
                                                .map((booking) => {

                                                    const top =
                                                        getBookingTop(
                                                            booking
                                                        );

                                                    const height =
                                                        getBookingHeight(
                                                            booking
                                                        );

                                                    return (

                                                        <div
                                                            key={
                                                                booking.practicePlanBookingId
                                                            }
                                                            className="planned-booking-block"
                                                            style={{
                                                                top:
                                                                    top + 4,

                                                                height:
                                                                    Math.max(
                                                                        40,
                                                                        height - 8
                                                                    ),
                                                            }}
                                                        >

                                                            <Group
                                                                justify="space-between"
                                                                wrap="nowrap"
                                                                align="flex-start"
                                                            >

                                                                <div>

                                                                    <Text
                                                                        fw={700}
                                                                        size="sm"
                                                                    >
                                                                        {
                                                                            booking.country
                                                                        }
                                                                    </Text>


                                                                    <Text
                                                                        size="xs"
                                                                        c="dimmed"
                                                                    >
                                                                        {
                                                                            booking.numberOfCourts
                                                                        }{" "}
                                                                        baner ·{" "}
                                                                        {
                                                                            booking.startTime.slice(
                                                                                0,
                                                                                5
                                                                            )
                                                                        }
                                                                        -
                                                                        {
                                                                            booking.endTime.slice(
                                                                                0,
                                                                                5
                                                                            )
                                                                        }
                                                                    </Text>


                                                                    {booking.numberOfPlayers !==
                                                                        undefined &&
                                                                        booking.numberOfPlayers !==
                                                                        null && (

                                                                            <Text
                                                                                size="xs"
                                                                                c="dimmed"
                                                                            >
                                                                                {
                                                                                    booking.numberOfPlayers
                                                                                }{" "}
                                                                                spillere
                                                                            </Text>

                                                                        )}

                                                                </div>


                                                                <button
                                                                    type="button"
                                                                    className="remove-planner-booking"
                                                                    title="Fjern fra planen"
                                                                    onClick={() =>
                                                                        removeBooking(
                                                                            booking
                                                                        )
                                                                    }
                                                                >
                                                                    ×
                                                                </button>

                                                            </Group>

                                                        </div>

                                                    );
                                                })}

                                        </div>

                                    </div>

                                ))}

                            </div>

                        </ScrollArea>

                    </Paper>

                </div>

            </Stack>

        </Container>
    );
};


export default PracticePlannerPage;